// GPU back-to-front sort for our WebGPU splat renderer (splatNodeMaterial.js)
// — replaces the CPU sort worker on that path. See WEBGPU-TSL-PORT-PLAN.md.
//
// Why: the CPU worker needs hundreds of ms to sort millions of splats, so
// while orbiting the draw order lags the camera (measured: three's addon,
// with its GPU sort, felt smoother in Chrome). Three's own addon sort is
// coarser, though — 4096 bins over the whole bounding sphere — which
// visibly muddied detail. This one keeps our WebGL path's precision:
//   - 65536 depth bins (same as the CPU worker's 16-bit counting sort)
//   - over the depth range of the splats actually VISIBLE this frame
//   - same visibility rules: in front of the camera, not negligibly small/
//     transparent (CPU worker's threshold), plus the vertex shader's own
//     on-screen test, so nothing it would draw is ever dropped.
// Reads the same centerAndScale/covAndColor textures the material does —
// no duplicate copy of the model, and the streamed pools (same texture
// layout) can reuse it later.
//
// Passes (each one compute dispatch):
//   reset → range (atomic min/max of visible distance) → histogram
//   → partial sums (256 blocks of 256 bins) → scan (one thread, also writes
//   the visible count into the indirect draw) → offsets → scatter.
// Splats sharing a bin land in arbitrary order, so the sort only re-runs
// when the camera (or the loaded splat count) changes — a still camera
// never reshuffles, so there's no flicker.

import { IndirectStorageBufferAttribute, Matrix4, StorageBufferAttribute } from 'three/webgpu';
import {
  Fn, If, Loop, abs, atomicAdd, atomicLoad, atomicMax, atomicMin, atomicStore,
  float, floatBitsToUint, instanceIndex, int, ivec2, max, min, sqrt, storage,
  textureLoad, uint, uniform, vec4,
} from 'three/tsl';

const BINS = 65536;
const BLOCKS = 256; // BINS = BLOCKS * BLOCKS
const WG = 256;
const CULLED = 0xffffffff;
const SIZE_THRESHOLD = 0.0001; // CPU worker: maxScale * alpha > 0.0001 * distance
const CLIP_BOUNDS = 1.2; // vertex shader's on-screen test

const u32 = (n) => new StorageBufferAttribute(new Uint32Array(n), 1, Uint32Array);

// sortDirThreshold: if set (cosine, e.g. 0.9995 like three's addon), only
// re-sort when the view direction turned past it (or the camera moved far
// relative to the scene) instead of on every camera change.
// prevRange: the histogram pass also collects this sort's depth range for the
// NEXT sort, which then skips the range pass (one full pass over the splats,
// texture reads and all). Splats beyond last sort's range land in the end
// bins for that one sort. The first sort runs the range pass.
// batch: all seven passes as ONE compute pass / submission instead of seven
// (WebKit pays per submission).
export function createSplatGpuSort({ count, centerAndScaleTexture, covAndColorTexture, textureWidth, indexed = true, sortDirThreshold = null, prevRange = false, batch = false }) {
  const modelView = uniform(new Matrix4());
  const projection = uniform(new Matrix4());
  const activeCount = uniform(0, 'uint');
  const width = uniform(textureWidth, 'uint');

  const orderAttr = u32(count);
  const orderWrite = storage(orderAttr, 'uint', count);
  const orderRead = storage(orderAttr, 'uint', count).toReadOnly();
  const binAttr = u32(count);
  const binWrite = storage(binAttr, 'uint', count);
  const binRead = storage(binAttr, 'uint', count).toReadOnly();
  const hist = storage(u32(BINS), 'uint', BINS).toAtomic();
  const offsets = storage(u32(BINS), 'uint', BINS).toAtomic();
  const partial = storage(u32(BLOCKS), 'uint', BLOCKS);
  const range = storage(u32(2), 'uint', 2).toAtomic(); // [min, max] distance bits
  const rangePrev = storage(u32(2), 'uint', 2); // last sort's range (prevRange)
  const usePrev = uniform(0, 'uint'); // 1: bin with rangePrev, skip the range pass

  // Indexed: drawIndexedIndirect args — indexCount 6 (two triangles over 4
  // shared corners), instanceCount, firstIndex, baseVertex, firstInstance.
  // Non-indexed: drawIndirect — vertexCount 6, instanceCount, firstVertex,
  // firstInstance. instanceCount is word 1 either way.
  const indirect = indexed
    ? new IndirectStorageBufferAttribute(new Uint32Array([6, 0, 0, 0, 0]), 5)
    : new IndirectStorageBufferAttribute(new Uint32Array([6, 0, 0, 0]), 4);
  const indirectWrite = storage(indirect, 'uint', indexed ? 5 : 4);

  const total = uint(count);

  // Visibility + camera distance of splat `i` (positive distance in front).
  const visibility = (i) => {
    const texPos = ivec2(int(i.mod(width)), int(i.div(width)));
    const centerAndScale = textureLoad(centerAndScaleTexture, texPos).toVar('cs');
    const cam = modelView.mul(vec4(centerAndScale.xyz, 1)).toVar('cam');
    const clip = projection.mul(cam).toVar('clip');
    const alpha = float(textureLoad(covAndColorTexture, texPos).w.shiftRight(uint(24))).div(255);
    // centerAndScale.w = maxCov / 32767 → sqrt(maxCov) ≈ largest axis scale.
    const sizeOpacity = sqrt(centerAndScale.w.mul(32767)).mul(alpha);
    const dist = cam.z.negate().toVar('dist');
    const bound = clip.w.mul(CLIP_BOUNDS);
    const visible = i.lessThan(activeCount)
      .and(alpha.mul(255).greaterThan(1)) // invisible at its peak — see splatNodeMaterial
      .and(dist.greaterThan(0))
      .and(sizeOpacity.greaterThan(dist.mul(SIZE_THRESHOLD)))
      .and(clip.z.greaterThanEqual(clip.w.negate()))
      .and(abs(clip.x).lessThanEqual(bound))
      .and(abs(clip.y).lessThanEqual(bound));
    return { visible, dist };
  };

  const reset = Fn(() => {
    atomicStore(hist.element(instanceIndex), uint(0));
    atomicStore(offsets.element(instanceIndex), uint(0));
    If(instanceIndex.equal(uint(0)), () => {
      if (prevRange) {
        // What the last sort's histogram collected becomes this sort's range.
        rangePrev.element(0).assign(atomicLoad(range.element(0)));
        rangePrev.element(1).assign(atomicLoad(range.element(1)));
      }
      atomicStore(range.element(0), uint(CULLED));
      atomicStore(range.element(1), uint(0));
    });
  })().compute(BINS, [WG]).setName('SplatSortReset');

  const rangePass = Fn(() => {
    If(instanceIndex.lessThan(total), () => {
      const { visible, dist } = visibility(instanceIndex);
      If(visible, () => {
        // Positive floats order the same as their bit patterns.
        const bits = floatBitsToUint(dist);
        atomicMin(range.element(0), bits);
        atomicMax(range.element(1), bits);
      });
    });
  })().compute(count, [WG]).setName('SplatSortRange');

  const histogram = Fn(() => {
    If(instanceIndex.lessThan(total), () => {
      const { visible, dist } = visibility(instanceIndex);
      const bin = uint(CULLED).toVar('bin');
      If(visible, () => {
        // Range as distance BITS. (A select() between an atomicLoad and a
        // plain load generated invalid WGSL — "expected a uint" — and the
        // whole sort failed to build; plain variables instead.)
        const nearBits = uint(0).toVar('nearBits');
        const farBits = uint(0).toVar('farBits');
        const bits = floatBitsToUint(dist);
        if (prevRange) {
          nearBits.assign(rangePrev.element(0));
          farBits.assign(rangePrev.element(1));
          If(usePrev.equal(uint(0)), () => {
            nearBits.assign(atomicLoad(range.element(0)));
            farBits.assign(atomicLoad(range.element(1)));
          });
          // Collect this sort's range for the next one.
          atomicMin(range.element(0), bits);
          atomicMax(range.element(1), bits);
        } else {
          nearBits.assign(atomicLoad(range.element(0)));
          farBits.assign(atomicLoad(range.element(1)));
        }
        // Bin 0 = farthest → drawn first (back to front). Bins spaced by the
        // distance's float bits (~logarithmic: fine up close, coarse far
        // away) rather than evenly — even spacing over a scene with far
        // floaters (Forest Village spans ~11 km) made bins ~17 cm deep, so
        // nearby surfaces shared a bin and their order flipped as the camera
        // moved. Clamped: with prevRange a splat can lie outside last sort's
        // range.
        const span = max(float(farBits).sub(float(nearBits)), 1);
        const t = min(max(float(farBits).sub(float(bits)).div(span), 0), 1);
        bin.assign(min(uint(t.mul(BINS - 1)), uint(BINS - 1)));
        atomicAdd(hist.element(bin), uint(1));
      });
      binWrite.element(instanceIndex).assign(bin);
    });
  })().compute(count, [WG]).setName('SplatSortHistogram');

  const partials = Fn(() => {
    const sum = uint(0).toVar('sum');
    const base = instanceIndex.mul(BLOCKS);
    Loop({ start: 0, end: BLOCKS, type: 'uint', name: 'k', condition: '<' }, ({ k }) => {
      sum.addAssign(atomicLoad(hist.element(base.add(k))));
    });
    partial.element(instanceIndex).assign(sum);
  })().compute(BLOCKS, [WG]).setName('SplatSortPartials');

  const scan = Fn(() => {
    const run = uint(0).toVar('run');
    Loop({ start: 0, end: BLOCKS, type: 'uint', name: 'k', condition: '<' }, ({ k }) => {
      const v = partial.element(k).toVar('v');
      partial.element(k).assign(run);
      run.addAssign(v);
    });
    indirectWrite.element(1).assign(run);
  })().compute(1).setName('SplatSortScan');

  const offsetPass = Fn(() => {
    const run = partial.element(instanceIndex).toVar('run');
    const base = instanceIndex.mul(BLOCKS);
    Loop({ start: 0, end: BLOCKS, type: 'uint', name: 'k', condition: '<' }, ({ k }) => {
      const idx = base.add(k);
      atomicStore(offsets.element(idx), run);
      run.addAssign(atomicLoad(hist.element(idx)));
    });
  })().compute(BLOCKS, [WG]).setName('SplatSortOffsets');

  const scatter = Fn(() => {
    If(instanceIndex.lessThan(total), () => {
      const bin = binRead.element(instanceIndex).toVar('bin');
      If(bin.notEqual(uint(CULLED)), () => {
        const dst = atomicAdd(offsets.element(bin), uint(1));
        orderWrite.element(dst).assign(instanceIndex);
      });
    });
  })().compute(count, [WG]).setName('SplatSortScatter');

  const lastModelView = new Matrix4();
  const lastProjection = new Matrix4();
  let lastActive = -1;
  let lastContentVersion = -1;
  let sorts = 0;
  const lastDir = [0, 0, 0];
  const lastPos = [0, 0, 0];

  return {
    // Read by the vertex shader: index of the splat to draw for each instance.
    orderRead,
    indirect,
    // Re-sorts when the view, the number of loaded splats, or the content
    // (contentVersion — streamed pool writes/frees) changed. Returns whether
    // it dispatched.
    update(renderer, camera, mesh, loadedCount, contentVersion = 0) {
      const mv = modelView.value.multiplyMatrices(camera.matrixWorldInverse, mesh.matrixWorld);
      const contentChanged = contentVersion !== lastContentVersion;
      if (!contentChanged && loadedCount === lastActive && mv.equals(lastModelView) && camera.projectionMatrix.equals(lastProjection)) {
        return false;
      }
      lastContentVersion = contentVersion;
      if (sortDirThreshold != null && !contentChanged && loadedCount === lastActive) {
        const e = mv.elements;
        const len = Math.hypot(e[2], e[6], e[10]) || 1;
        const d = [e[2] / len, e[6] / len, e[10] / len];
        const dot = d[0] * lastDir[0] + d[1] * lastDir[1] + d[2] * lastDir[2];
        const moved = Math.hypot(e[12] - lastPos[0], e[13] - lastPos[1], e[14] - lastPos[2]);
        const dist = Math.hypot(e[12], e[13], e[14]) || 1;
        if (dot > sortDirThreshold && moved < dist * 0.02) return false;
      }
      { const e = mv.elements; const len = Math.hypot(e[2], e[6], e[10]) || 1;
        lastDir[0] = e[2] / len; lastDir[1] = e[6] / len; lastDir[2] = e[10] / len;
        lastPos[0] = e[12]; lastPos[1] = e[13]; lastPos[2] = e[14]; }
      lastModelView.copy(mv);
      lastProjection.copy(camera.projectionMatrix);
      lastActive = loadedCount;
      projection.value.copy(camera.projectionMatrix);
      activeCount.value = loadedCount;
      const skipRange = prevRange && sorts > 0;
      usePrev.value = skipRange ? 1 : 0;
      sorts++;
      const passes = skipRange
        ? [reset, histogram, partials, scan, offsetPass, scatter]
        : [reset, rangePass, histogram, partials, scan, offsetPass, scatter];
      if (batch) renderer.compute(passes);
      else passes.forEach((p) => renderer.compute(p));
      return true;
    },
  };
}
